import { type MaybeRef, toValue } from 'vue';

import {
  addDatabaseFirewallRules,
  convertDatabaseBackup,
  createDatabase,
  createDatabaseSnapshot,
  deleteDatabase,
  deleteDatabaseFirewallRules,
  deleteDatabaseSnapshot,
  disableDatabaseBackups,
  enableDatabaseBackups,
  powerCycleDatabase,
  powerOffDatabase,
  powerOnDatabase,
  renameDatabase,
  resetDatabasePassword,
  resizeDatabase,
  restoreDatabase,
  saveDatabaseTags,
} from './api';

export const createDatabaseMutation = () => ({
  mutationFn: (data: {
    name: string;
    engine: string;
    region: string;
    size: string;
    project_uuid?: string;
    tags: string[];
  }) => createDatabase(data),
});

export const powerOnDatabaseMutation = (databaseUuid: MaybeRef<string>) => ({
  mutationFn: () => powerOnDatabase(toValue(databaseUuid)),
});

export const powerOffDatabaseMutation = (databaseUuid: MaybeRef<string>) => ({
  mutationFn: () => powerOffDatabase(toValue(databaseUuid)),
});

export const powerCycleDatabaseMutation = (databaseUuid: MaybeRef<string>) => ({
  mutationFn: () => powerCycleDatabase(toValue(databaseUuid)),
});

export const renameDatabaseMutation = (databaseUuid: MaybeRef<string>) => ({
  mutationFn: (name: string) => renameDatabase(toValue(databaseUuid), name),
});

export const resetDatabasePasswordMutation = (databaseUuid: MaybeRef<string>) => ({
  mutationFn: () => resetDatabasePassword(toValue(databaseUuid)),
});

export const saveDatabaseTagsMutation = (databaseUuid: MaybeRef<string>) => ({
  mutationFn: (data: { tags: string[] }) => saveDatabaseTags(toValue(databaseUuid), data),
});

export const enableDatabaseBackupsMutation = (databaseUuid: MaybeRef<string>) => ({
  mutationFn: () => enableDatabaseBackups(toValue(databaseUuid)),
});

export const disableDatabaseBackupsMutation = (databaseUuid: MaybeRef<string>) => ({
  mutationFn: () => disableDatabaseBackups(toValue(databaseUuid)),
});

export const convertDatabaseBackupMutation = (databaseUuid: MaybeRef<string>) => ({
  mutationFn: (backupId: number) => convertDatabaseBackup(toValue(databaseUuid), backupId),
});

export const deleteDatabaseMutation = (databaseUuid: MaybeRef<string>) => ({
  mutationFn: () => deleteDatabase(toValue(databaseUuid)),
});

export const deleteDatabasesMutation = () => ({
  mutationFn: (uuids: string[]) => Promise.all(uuids.map((uuid) => deleteDatabase(uuid))),
});

export const resizeDatabaseMutation = (databaseUuid: MaybeRef<string>) => ({
  mutationFn: (size: string) => resizeDatabase(toValue(databaseUuid), size),
});

export const createDatabaseSnapshotMutation = (databaseUuid: MaybeRef<string>) => ({
  mutationFn: (name: string) => createDatabaseSnapshot(toValue(databaseUuid), name),
});

export const deleteDatabaseSnapshotMutation = (databaseUuid: MaybeRef<string>) => ({
  mutationFn: (snapshotId: number) => deleteDatabaseSnapshot(toValue(databaseUuid), snapshotId),
});

export const deleteDatabaseSnapshotsMutation = (databaseUuid: MaybeRef<string>) => ({
  mutationFn: (ids: number[]) =>
    Promise.all(ids.map((id) => deleteDatabaseSnapshot(toValue(databaseUuid), id))),
});

export const restoreDatabaseMutation = (databaseUuid: MaybeRef<string>) => ({
  mutationFn: (imageId: number) => restoreDatabase(toValue(databaseUuid), imageId),
});

export const addDatabaseFirewallRulesMutation = (databaseUuid: MaybeRef<string>) => ({
  mutationFn: (data: { rules: { address: string }[] }) =>
    addDatabaseFirewallRules(toValue(databaseUuid), data),
});

export const deleteDatabaseFirewallRulesMutation = (databaseUuid: MaybeRef<string>) => ({
  mutationFn: (rules: { address: string }[]) =>
    deleteDatabaseFirewallRules(toValue(databaseUuid), rules),
});
